/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import TrainerCard from "@/components/TrainerCard";
import RecruitModal from "@/components/RecruitModal";
import TrainerDetailModal from "@/components/TrainerDetailModal";
import TradeModal from "@/components/TradeModal";
import ForceActionModal from "@/components/ForceActionModal";
import { UserPlus, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

export default function TrainersPage() {
  const [trainers, setTrainers] = useState([]);
  const [ref, setRef] = useState({ locations: {}, gyms: [] });
  const [prog, setProg] = useState(null);
  const [recruitOpen, setRecruitOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailTab, setDetailTab] = useState("team");
  const [forceAction, setForceAction] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, r, p] = await Promise.all([
        api.get("/trainers"), api.get("/ref"), api.get("/progression"),
      ]);
      setTrainers(t.data);
      setRef(r.data);
      setProg(p.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const setStrategy = async (id, strategy) => {
    if (["explore", "capture", "train"].includes(strategy)) {
      setForceAction({ id, action: strategy });
      return;
    }
    try {
      await api.post(`/trainers/${id}/strategy`, { strategy });
      if (strategy === "rest") toast.success("Repos forcé — placé en prochaine action.");
      await load();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dismiss = async (id) => {
    if (!window.confirm("Renvoyer ce dresseur ?")) return;
    try { await api.delete(`/trainers/${id}`); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const usedSlots = trainers.length;
  const maxSlots = prog?.unlocked_slot_count ?? 1;

  return (
    <div data-testid="trainers-page">
      <div className="flex justify-between items-end mb-4 gap-4 flex-wrap">
        <div>
          <div className="overline">Personnel du labo · {usedSlots}/{maxSlots} slots</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Dresseurs</h1>
          <p className="text-xs text-[#4A4A4A] mt-2 max-w-2xl">
            Chaque dresseur poursuit son aventure en temps réel selon sa personnalité.
            Modifiez sa stratégie pour le rediriger immédiatement.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="trade-open-btn" className="btn-ink" onClick={() => setTradeOpen(true)} disabled={trainers.length < 2}>
            <ArrowLeftRight size={14}/> Échanger
          </button>
          <button data-testid="trainers-recruit-btn" className="btn-ink btn-leaf" onClick={() => setRecruitOpen(true)} disabled={usedSlots >= maxSlots}>
            <UserPlus size={14}/> Recruter
          </button>
        </div>
      </div>

      {trainers.length === 0 ? (
        <div className="panel p-8 text-center text-[#7A7A7A] font-mono text-sm">
          Aucun dresseur au labo. Recrutez votre premier candidat pour démarrer l&apos;aventure.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {trainers.map((t) => (
            <TrainerCard
              key={t.id}
              trainer={t}
              locationsRef={ref.locations}
              gymsRef={ref.gyms}
              onStrategy={setStrategy}
              onDismiss={dismiss}
              onOpenDetail={(tid) => { setDetailTab("team"); setDetailId(tid); }}
              onOpenBag={(tid) => { setDetailTab("bag"); setDetailId(tid); }}
            />
          ))}
        </div>
      )}

      <RecruitModal open={recruitOpen} onClose={() => setRecruitOpen(false)} onRecruited={load}/>
      <TrainerDetailModal trainerId={detailId} open={!!detailId} onClose={() => setDetailId(null)} regions={prog?.regions || []} initialTab={detailTab}/>
      <TradeModal open={tradeOpen} onClose={() => setTradeOpen(false)} onTraded={load}/>
      <ForceActionModal
        trainerId={forceAction?.id}
        action={forceAction?.action}
        open={!!forceAction}
        onClose={() => setForceAction(null)}
        onAfterSubmit={load}
      />
    </div>
  );
}
